import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useEffect, useState } from 'react';
import { useNotify, useTranslate } from 'react-admin';
import { DisplayTextFields } from '../../perps-v1-shared/DisplayTextFields';
import { isValidCode, SECTOR_CODE_MAX_LENGTH } from '../../perps-v1-shared/codeField';
import { errorMessage } from '../../perps-v1-shared/errorMessage';
import { DisplayText, MarketSector } from '../../perps-v1-shared/types';
import { sectorsApi } from '../dataProvider';

const SCOPES = ['native', 'hip3', 'any'] as const;

interface FormState {
  code: string;
  name: DisplayText;
  scope: (typeof SCOPES)[number];
  displayOrder: string;
}

// 空白默认值：与后端 DTO 必填字段对齐（scope/displayOrder）
//
// `displayOrder` defaults to '1' for callers that don't care. Real creates MUST pass
// List.tsx's `nextDisplayOrder(sectors)` (max existing displayOrder + 1) so a new sector lands
// at the end instead of colliding with whatever currently holds `1` — there is no longer a
// Display order input for the admin to set this by hand.
function blankForm(displayOrder: string = '1'): FormState {
  return {
    code: '',
    name: {},
    scope: 'native',
    displayOrder,
  };
}

function formFromSector(sector: MarketSector): FormState {
  return {
    code: sector.code,
    name: sector.name ?? {},
    scope: sector.scope,
    displayOrder: String(sector.displayOrder ?? 1),
  };
}

interface Props {
  open: boolean;
  sector: MarketSector | null;
  // displayOrder to seed a new sector with — List.tsx computes max(sectors' displayOrder) + 1
  // (see nextDisplayOrder) so a new sector always lands at the end of the list. There is no
  // longer a Display order input for the admin to edit this by hand. Ignored in edit mode, where
  // the sector's existing displayOrder is preserved untouched via formFromSector.
  createDisplayOrder: number;
  onClose: () => void;
  onSaved: () => void;
}

export default function SectorEditorDialog({
  open,
  sector,
  createDisplayOrder,
  onClose,
  onSaved,
}: Props) {
  const translate = useTranslate();
  const notify = useNotify();
  const isEdit = Boolean(sector);

  const [form, setForm] = useState<FormState>(
    sector ? formFromSector(sector) : blankForm(String(createDisplayOrder))
  );
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(sector ? formFromSector(sector) : blankForm(String(createDisplayOrder)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, sector, createDisplayOrder]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  // Task ERRTOAST: same treatment as the New view dialog — check the code shape here rather than
  // printing the backend DTO's raw regex and letting the server reject it. Create-mode only; the
  // input is disabled when editing an existing sector.
  const trimmedCode = form.code.trim();
  const codeInvalid =
    !isEdit && trimmedCode.length > 0 && !isValidCode(trimmedCode, SECTOR_CODE_MAX_LENGTH);

  const handleSave = async () => {
    setSaving(true);
    try {
      const body = {
        code: form.code.trim(),
        name: form.name,
        scope: form.scope,
        displayOrder: String(form.displayOrder).trim() ? Number(form.displayOrder) : 1,
      };

      if (isEdit && sector) {
        await sectorsApi.update(sector.code, body);
      } else {
        await sectorsApi.create(body);
      }
      notify(translate('module.perpsV1Shared.saveSuccess'), { type: 'success' });
      onSaved();
      onClose();
    } catch (error: any) {
      notify(
        errorMessage(error, translate('module.perpsV1Shared.saveError')),
        { type: 'error' }
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isEdit
          ? translate('module.perpsV1Sectors.editSector')
          : translate('module.perpsV1Sectors.newSector')}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5} sx={{ mt: 0.5 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label={translate('module.perpsV1Sectors.fields.code')}
                value={form.code}
                disabled={isEdit}
                onChange={(e) => set('code', e.target.value)}
                error={codeInvalid}
                helperText={translate('module.perpsV1Shared.codeRule', {
                  max: SECTOR_CODE_MAX_LENGTH,
                })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                size="small"
                label={translate('module.perpsV1Sectors.fields.scope')}
                value={form.scope}
                onChange={(e) => set('scope', e.target.value as FormState['scope'])}
              >
                <MenuItem value="native">{translate('module.perpsV1Sectors.scopeNative')}</MenuItem>
                <MenuItem value="hip3">{translate('module.perpsV1Sectors.scopeHip3')}</MenuItem>
                <MenuItem value="any">{translate('module.perpsV1Sectors.scopeAny')}</MenuItem>
              </TextField>
            </Grid>
          </Grid>

          <Stack spacing={1}>
            <Typography variant="subtitle2">
              {translate('module.perpsV1Sectors.fields.nameGroup')}
            </Typography>
            <DisplayTextFields
              value={form.name}
              onChange={(v) => set('name', v)}
              labelPrefix="module.perpsV1Shared.label"
              slugPlaceholder={`market.sector.${form.code || 'code'}`}
            />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <LoadingButton onClick={onClose} disabled={saving}>
          {translate('module.perpsV1Shared.cancel')}
        </LoadingButton>
        <LoadingButton
          variant="contained"
          onClick={handleSave}
          loading={saving}
          disabled={!trimmedCode || !form.scope || codeInvalid}
        >
          {translate('module.perpsV1Shared.save')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
